import { AuthQueryProvider } from "@daveyplate/better-auth-tanstack";
import type { QueryClient } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	HeadContent,
	Link,
	Outlet,
	Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { Toaster } from "sonner";

import DevTools from "@/components/DevTools";
import { LazyMotionProvider } from "@/components/LazyMotionProvider";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { useSession } from "@/hooks/auth-hooks";
import { useThemeShortcut } from "@/hooks/use-theme-shortcut";
import { confirmPendingAuthProvider } from "@/lib/last-auth-provider";
import { ThemeProvider } from "@/utils/themeProvider";

import "@fontsource/geist/400.css";
import "@fontsource/geist/500.css";
import "@fontsource/geist/600.css";
import "@fontsource/geist-mono/400.css";
import "@fontsource/geist-mono/500.css";
import geistWoff2 from "@fontsource/geist/files/geist-latin-400-normal.woff2?url";

import appCss from "../styles.css?url";

interface MyRouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content:
					"width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover",
			},
			{
				title: "AgentFramer, MCP-Native Media Generation for AI Agents",
			},
		],
		links: [
			{
				rel: "preload",
				href: geistWoff2,
				as: "font",
				type: "font/woff2",
				crossOrigin: "anonymous",
			},
			{
				rel: "stylesheet",
				href: "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap",
			},
			{
				rel: "stylesheet",
				href: "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200",
			},
			{
				rel: "stylesheet",
				href: appCss,
			},
			{
				rel: "icon",
				type: "image/svg+xml",
				href: "/favicon.svg",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "32x32",
				href: "/favicon-32.png",
			},
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{
				rel: "manifest",
				href: "/manifest.json",
			},
		],
	}),

	shellComponent: RootDocument,
	notFoundComponent: NotFound,
});

function NotFound() {
	const { session } = useSession();
	const homeTo = session ? "/dashboard" : "/";

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />

			<main className="flex flex-1 items-center justify-center px-4 py-20 pt-28 sm:py-28 sm:pt-36 min-h-[60vh]">
				<div className="flex flex-col items-center text-center">
					<p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
						Error 404
					</p>
					<h1 className="mt-4 text-6xl font-semibold tracking-[-0.04em] text-foreground sm:text-7xl">
						Page not found
					</h1>
					<p className="mt-6 max-w-md text-base text-muted-foreground">
						The page you are looking for doesn't exist or has been moved.
						{session
							? " Head back to your dashboard to keep working."
							: " Head back to the homepage to find what you need."}
					</p>
					<div className="mt-10 flex flex-wrap items-center justify-center gap-3">
						<Link
							to={homeTo}
							className="inline-flex h-10 items-center rounded-md bg-brand-accent px-5 text-sm font-medium text-white transition-colors hover:bg-brand-accent/90"
						>
							{session ? "Return to dashboard" : "Return home"}
						</Link>
						<Link
							to="/help"
							className="inline-flex h-10 items-center rounded-md border border-border bg-background px-5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
						>
							Browse the docs
						</Link>
					</div>
				</div>
			</main>

			<PublicFooter />
		</div>
	);
}

function ThemeShortcut() {
	useThemeShortcut();
	return null;
}

function ConfirmAuthProvider() {
	const { session } = useSession();
	useEffect(() => {
		confirmPendingAuthProvider(!!session);
	}, [session]);
	return null;
}

function RootDocument() {
	useEffect(() => {
		if (import.meta.env.DEV) {
			void import("react-grab");
			void import("@react-grab/claude-code/client");
		}
	}, []);

	return (
		<html lang="en" suppressHydrationWarning>
			<head>
				<HeadContent />
			</head>
			<body>
				<a
					href="#main-content"
					className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-white focus:bg-[#6B3FF2]"
				>
					Skip to main content
				</a>
				<ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
					<ThemeShortcut />
					<ConfirmAuthProvider />
					<LazyMotionProvider>
						<AuthQueryProvider>
							<Outlet />
							<DevTools />
							<Toaster
								position="top-center"
								icons={{
									success: (
										<span
											className="material-symbols-outlined"
											aria-hidden="true"
											style={{
												fontSize: "18px",
												color: "#2b9a66",
												fontVariationSettings: "'FILL' 1",
											}}
										>
											check_circle
										</span>
									),
									error: (
										<span
											className="material-symbols-outlined"
											aria-hidden="true"
											style={{
												fontSize: "18px",
												color: "#ff6e84",
												fontVariationSettings: "'FILL' 1",
											}}
										>
											cancel
										</span>
									),
									info: (
										<span
											className="material-symbols-outlined"
											aria-hidden="true"
											style={{
												fontSize: "18px",
												color: "#6B3FF2",
												fontVariationSettings: "'FILL' 1",
											}}
										>
											info
										</span>
									),
									warning: (
										<span
											className="material-symbols-outlined"
											aria-hidden="true"
											style={{
												fontSize: "18px",
												color: "#f59e0b",
												fontVariationSettings: "'FILL' 1",
											}}
										>
											warning
										</span>
									),
								}}
								toastOptions={{
									style: {
										fontFamily: "'Geist', -apple-system, sans-serif",
										gap: "16px",
										backgroundColor: "#111111",
										boxShadow:
											"rgba(255,255,255,0.06) 0px 0px 0px 1px, rgba(0,0,0,0.4) 0px 4px 12px 0px",
										border: "none",
										color: "#fafafa",
										borderRadius: "8px",
									},
									classNames: {
										title: "font-bold",
										description: "text-[#adaaaa]",
									},
								}}
							/>
						</AuthQueryProvider>
					</LazyMotionProvider>
				</ThemeProvider>
				<Scripts />
			</body>
		</html>
	);
}
